import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export type Locale = "en" | "pl" | "ru";

const dict: Record<Locale, Record<string, string>> = {
  en: {
    // Header
    "header.title": "RESTIFY · Dashboard",
    "header.signout": "Sign out",
    // Billing
    "billing.trial_days": "Trial: {days} days left.",
    "billing.trial_expired": "Your trial has ended.",
    "billing.trial_hint": "Pick a plan to keep every feature enabled.",
    "billing.upgrade": "Choose a plan",
    "billing.unavailable": "Billing is not available yet — contact support.",
    "billing.error": "Could not start checkout",
    // Login
    "login.title": "RESTIFY",
    "login.subtitle": "Sign in to your dashboard",
    "login.subtitle_register": "Create your account",
    "login.email": "Email",
    "login.password": "Password",
    "login.fullname": "Full Name",
    "login.signin": "Sign in",
    "login.signing_in": "Signing in…",
    "login.create": "Create Account",
    "login.creating": "Creating account…",
    "login.toggle_register": "Don't have an account? Create one",
    "login.toggle_login": "Already have an account? Sign in",
    // Tabs
    "tab.orders": "Live Orders",
    "tab.analytics": "Analytics",
    "tab.integrations": "Integrations",
    "tab.menu": "Menu",
    "tab.settings": "Settings",
    "tab.help": "Help",
    // Orders
    "orders.title": "Live Orders",
    "orders.search": "Search orders, items, customers…",
    "orders.empty": "No orders yet — waiting for webhooks…",
    "orders.empty_filter": "No orders match your filters",
    "orders.connect": "🔄 Refresh Screen",
    "orders.all_platforms": "All Platforms",
    "orders.all_statuses": "All Statuses",
    "orders.accept": "✅ Accept",
    "orders.reject": "❌ Reject",
    "orders.hide": "✕ Hide",
    "orders.clear_completed": "🧹 Clear",
    "orders.print": "🖨️ Print",
    // Analytics
    "analytics.title": "Analytics · Last 14 days",
    "analytics.total": "Total Orders",
    "analytics.revenue": "Revenue",
    "analytics.avg": "Avg Order",
    "analytics.cancelled": "Cancelled",
    "analytics.active": "Active Now",
    // Advanced Analytics
    "adv_analytics.days": "days",
    "adv_analytics.export_csv": "Export CSV",
    "adv_analytics.export_pdf": "Print / PDF",
    "adv_analytics.export_failed": "Export failed",
    "adv_analytics.hourly_title": "Orders by Hour of Day",
    "adv_analytics.platform_revenue": "Revenue by Platform",
    "adv_analytics.avg_check_title": "Average Check by Platform",
    "adv_analytics.avg_check": "Avg Check",
    "adv_analytics.top_dishes_title": "Top Dishes",
    "adv_analytics.dish_name": "Dish",
    "adv_analytics.quantity": "Qty Sold",
    "adv_analytics.platform": "Platform",
    "adv_analytics.reconciliation_title": "Revenue Reconciliation",
    "adv_analytics.fees": "Fees",
    "adv_analytics.net": "Net",
    "adv_analytics.no_data": "No data for selected period.",
    // Integrations
    "integrations.title": "Integrations",
    "integrations.add": "+ Add Integration",
    "integrations.desc": "Manage your delivery platform connections.",
    "integrations.empty": "No integrations connected yet.",
    "integrations.wizard_title": "Connect a Service",
    "integrations.wizard_desc": "Select the delivery platform you want to integrate.",
    "integrations.wizard_creds": "Enter your venue credentials from the provider portal.",
    "integrations.ext_id": "External Restaurant ID",
    "integrations.api_key": "API Key / Secret",
    "integrations.continue": "Continue",
    "integrations.back": "Back",
    "integrations.complete": "Complete Setup",
    "integrations.connecting": "Connecting…",
    "integrations.sandbox_mode": "Sandbox Mode",
    "integrations.edit": "Edit",
    "integrations.delete": "Delete",
    "integrations.delete_confirm": "Are you sure you want to hard delete this integration? This cannot be undone.",
    "integrations.updated": "Integration updated",
    "integrations.deleted": "Integration deleted",
    // Menu
    "menu.title": "Menu",
    "menu.products": "Products",
    "menu.attributes": "Attributes",
    "menu.add_product": "+ Add Product",
    "menu.add_attribute": "+ Add Attribute",
    "menu.name": "Name",
    "menu.sku": "SKU",
    "menu.price": "Price (minor)",
    "menu.description": "Description",
    "menu.active": "Active",
    "menu.actions": "Actions",
    "menu.save": "Save",
    "menu.cancel": "Cancel",
    "menu.delete": "Delete",
    "menu.empty": "No menu items yet. Add your first product.",
    "menu.category": "Category",
    "menu.add_category": "+ Add Category",
    "menu.category_name": "Category name",
    "menu.attr_name": "Attribute name",
    // Settings
    "settings.title": "Settings",
    "settings.language": "Language",
    "settings.language_desc": "Choose your preferred interface language.",
    "settings.printer": "Printer Configuration",
    "settings.printer_desc": "Set up receipt printing for your orders.",
    "settings.printer_ip": "Printer IP Address",
    "settings.printer_name": "Printer Name",
    "settings.test_print": "🖨️ Test Print",
    "settings.auto_print": "Auto-Print Settings",
    "settings.auto_new": "Auto-print on new order received",
    "settings.auto_accept": "Auto-print on order accepted",
    "settings.auto_reject": "Auto-print on order rejected",
    "settings.save": "Save Settings",
    "settings.saved": "Settings saved!",
    "settings.team": "Team",
    "settings.team_desc": "Who can see orders and manage this restaurant.",
    "settings.team_members": "Members",
    "settings.team_invite": "Invite a teammate",
    "settings.team_invite_email": "Email",
    "settings.team_invite_role": "Role",
    "settings.team_invite_send": "Send invitation",
    "settings.team_inviting": "Creating…",
    "settings.team_pending": "Pending invitations",
    "settings.team_no_pending": "No pending invitations.",
    "settings.team_link_ready": "Invitation e-mailed to {email}. If it doesn't arrive, copy this link.",
    "settings.team_copy": "Copy link",
    "settings.team_revoke": "Revoke",
    "settings.team_remove": "Remove",
    "settings.team_remove_confirm": "Remove {email} from this restaurant? They will lose access immediately.",
    "settings.team_removed": "Member removed",
    "settings.team_invited": "Invitation created",
    "settings.team_revoked": "Invitation revoked",
    "settings.team_link_copied": "Link copied",
    "settings.team_only_managers": "Only managers and owners can invite or remove people.",
    "settings.team_you": "you",
    // Help
    "help.title": "Help Center",
    "help.faq": "Frequently Asked Questions",
    "help.contact": "Contact Us",
    "help.faq1_q": "How to connect a delivery service?",
    "help.faq1_a": "Go to the Integrations tab and click '+ Add Integration'. Select your platform (Wolt, Glovo, Uber Eats, etc.), enter your venue credentials from the provider portal, and click 'Complete Setup'. Your orders will start flowing in automatically.",
    "help.faq2_q": "How to accept or reject orders?",
    "help.faq2_a": "New orders arrive with 'received' status. Click '✅ Accept' to confirm the order and start preparing it, or '❌ Reject' to decline it. The status change is pushed to the delivery platform in real-time.",
    "help.faq3_q": "How to set up auto-print?",
    "help.faq3_a": "Go to Settings → Printer Configuration. Enter your printer IP address and name, then enable the auto-print toggles for the events you want (new order, accepted, rejected). Click 'Save Settings'.",
    "help.faq4_q": "How to manage my menu?",
    "help.faq4_a": "Go to the Menu tab. You can add products with name, SKU, and price. Toggle the 'Active' switch to enable/disable items across all connected delivery platforms simultaneously.",
    "help.faq5_q": "How to change the interface language?",
    "help.faq5_a": "Go to Settings → Language and select your preferred language. The change applies immediately across the entire interface.",
    "help.faq6_q": "How does multi-platform sync work?",
    "help.faq6_a": "When you toggle a product's availability in Restify, the change is pushed to all connected platforms (Wolt, Glovo, etc.) simultaneously. This means you only need to manage your menu in one place.",
    // Toasts
    "toast.order_received": "New Order",
    "toast.order_received_body": "Order #{id} received",
    "toast.status_updated": "Status Updated",
    "toast.error": "Error",
    "toast.connected": "Connected",
    "toast.settings_saved": "Settings Saved",
    // Generic
    "generic.loading": "Loading…",
    "generic.orders": "orders",
    "generic.no_membership": "Your account doesn't have a restaurant membership yet — ask an owner to invite you.",
    // Onboarding Wizard
    "onboarding.step_welcome": "Welcome",
    "onboarding.step_connect": "Connect",
    "onboarding.step_test": "Test",
    "onboarding.step_live": "Go Live",
    "onboarding.step_complete": "Done",
    "onboarding.welcome_title": "Let's set up your restaurant!",
    "onboarding.welcome_desc": "We'll guide you through connecting your first delivery platform and verifying that orders flow in correctly.",
    "onboarding.checklist_connect": "Connect a delivery platform",
    "onboarding.checklist_connect_desc": "Link your Wolt, Uber Eats, Glovo, Pyszne.pl or Bolt Food account.",
    "onboarding.checklist_test": "Send a test order",
    "onboarding.checklist_test_desc": "Verify the integration works with a sandbox simulation.",
    "onboarding.checklist_live": "Go live",
    "onboarding.checklist_live_desc": "Disable sandbox and start receiving real orders.",
    "onboarding.get_started": "Get Started",
    "onboarding.connect_title": "Connect a delivery platform",
    "onboarding.connect_desc": "Select the platform you want to integrate first. You can add more later.",
    "onboarding.change_platform": "Change",
    "onboarding.ext_id_placeholder": "e.g. 12345",
    "onboarding.chain_id_label": "Delivery Hero chain ID (optional)",
    "onboarding.chain_id_placeholder": "e.g. chain-12345",
    "onboarding.chain_id_hint": "Glovo only. Leave empty for the legacy Glovo API.",
    "onboarding.secret_placeholder": "Paste your secret key here",
    "onboarding.optional_for_sandbox": "optional for sandbox",
    "onboarding.webhook_url_label": "Webhook URL (register in provider portal)",
    "onboarding.webhook_url_hint": "Copy this URL and register it in your delivery platform's webhook settings.",
    "onboarding.copy": "Copy",
    "onboarding.connect_and_test": "Connect & Continue",
    "onboarding.test_title": "Test your connection",
    "onboarding.test_desc": "Your integration is in sandbox mode. Let's send a simulated order to make sure everything works.",
    "onboarding.sandbox_active": "SANDBOX",
    "onboarding.send_test_order": "Send Test Order",
    "onboarding.sending_test": "Sending…",
    "onboarding.test_success": "Test order sent!",
    "onboarding.test_success_desc": "The simulated order was processed successfully. Check the Live Orders tab to see it.",
    "onboarding.retry": "Try Again",
    "onboarding.go_live_title": "Enable real orders",
    "onboarding.go_live_desc": "Sandbox test passed. Enter the real credentials from the provider portal and enable live orders. Live mode is blocked until all checks pass.",
    "onboarding.live_secret_label": "Real webhook secret",
    "onboarding.live_secret_placeholder": "Real secret from the provider portal",
    "onboarding.live_secret_hint": "Leave empty if you already entered the real secret in the previous step.",
    "onboarding.live_readiness": "Connection checks",
    "onboarding.live_not_ready": "The connection is not ready yet — fix the failed checks and re-check.",
    "onboarding.live_oauth_pending": "Wolt OAuth is not complete. Finish the authorization on the Wolt side and press re-check.",
    "onboarding.check_external_restaurant_id": "Provider venue/store ID",
    "onboarding.check_webhook_secret": "Real webhook secret",
    "onboarding.check_provider_config": "Provider configuration",
    "onboarding.check_oauth": "OAuth tokens",
    "onboarding.refresh_readiness": "Re-check",
    "onboarding.go_live": "Enable live orders",
    "onboarding.going_live": "Enabling…",
    "onboarding.complete_title": "You're all set!",
    "onboarding.complete_desc": "Your restaurant is connected and ready to receive orders.",
    "onboarding.summary_restaurant": "Restaurant created",
    "onboarding.summary_integration": "{platform} connected",
    "onboarding.summary_test": "Test order verified",
    "onboarding.next_steps": "Recommended next steps",
    "onboarding.next_webhook": "Register the webhook URL in your provider portal to receive real orders.",
    "onboarding.next_more_platforms": "Connect additional delivery platforms from the Integrations tab.",
    "onboarding.next_printer": "Set up a receipt printer in Settings for automatic order printing.",
    "onboarding.next_menu": "Import your menu in the Menu tab to sync availability across platforms.",
    "onboarding.go_to_dashboard": "Go to Dashboard",
    "onboarding.skip": "Skip setup — I'll do it later",
  },
  pl: {
    "header.title": "RESTIFY · Panel",
    "header.signout": "Wyloguj",
    // Billing
    "billing.trial_days": "Okres próbny: pozostało {days} dni.",
    "billing.trial_expired": "Twój okres próbny się zakończył.",
    "billing.trial_hint": "Wybierz plan, aby zachować wszystkie funkcje.",
    "billing.upgrade": "Wybierz plan",
    "billing.unavailable": "Płatności nie są jeszcze dostępne — skontaktuj się z pomocą.",
    "billing.error": "Nie udało się rozpocząć płatności",
    "login.title": "RESTIFY",
    "login.subtitle": "Zaloguj się do panelu",
    "login.subtitle_register": "Utwórz konto",
    "login.email": "E-mail",
    "login.password": "Hasło",
    "login.fullname": "Imię i nazwisko",
    "login.signin": "Zaloguj",
    "login.signing_in": "Logowanie…",
    "login.create": "Utwórz konto",
    "login.creating": "Tworzenie…",
    "login.toggle_register": "Nie masz konta? Utwórz",
    "login.toggle_login": "Masz już konto? Zaloguj",
    "tab.orders": "Zamówienia",
    "tab.analytics": "Analityka",
    "tab.integrations": "Integracje",
    "tab.menu": "Menu",
    "tab.settings": "Ustawienia",
    "tab.help": "Pomoc",
    "orders.title": "Zamówienia na żywo",
    "orders.search": "Szukaj zamówień, produktów, klientów…",
    "orders.empty": "Brak zamówień — oczekiwanie na webhooki…",
    "orders.empty_filter": "Brak zamówień pasujących do filtrów",
    "orders.connect": "🔄 Odśwież ekran",
    "orders.all_platforms": "Wszystkie platformy",
    "orders.all_statuses": "Wszystkie statusy",
    "orders.accept": "✅ Przyjmij",
    "orders.reject": "❌ Odrzuć",
    "orders.hide": "✕ Ukryj",
    "orders.clear_completed": "🧹 Wyczyść",
    "orders.print": "🖨️ Drukuj",
    "analytics.title": "Analityka · Ostatnie 14 dni",
    "analytics.total": "Łącznie zamówień",
    "analytics.revenue": "Przychód",
    "analytics.avg": "Śr. zamówienie",
    "analytics.cancelled": "Anulowane",
    "analytics.active": "Aktywne teraz",
    "adv_analytics.days": "dni",
    "adv_analytics.export_csv": "Eksport CSV",
    "adv_analytics.export_pdf": "Drukuj / PDF",
    "adv_analytics.export_failed": "Eksport nie powiódł się",
    "adv_analytics.hourly_title": "Zamówienia wg godziny",
    "adv_analytics.platform_revenue": "Przychód wg platformy",
    "adv_analytics.avg_check_title": "Średni rachunek wg platformy",
    "adv_analytics.avg_check": "Śr. rachunek",
    "adv_analytics.top_dishes_title": "Najpopularniejsze dania",
    "adv_analytics.dish_name": "Danie",
    "adv_analytics.quantity": "Sprzedano",
    "adv_analytics.platform": "Platforma",
    "adv_analytics.reconciliation_title": "Rozliczenie przychodów",
    "adv_analytics.fees": "Opłaty",
    "adv_analytics.net": "Netto",
    "adv_analytics.no_data": "Brak danych za wybrany okres.",
    "integrations.title": "Integracje",
    "integrations.add": "+ Dodaj integrację",
    "integrations.desc": "Zarządzaj połączeniami z platformami dostaw.",
    "integrations.empty": "Brak podłączonych integracji.",
    "integrations.wizard_title": "Podłącz serwis",
    "integrations.wizard_desc": "Wybierz platformę dostawczą do integracji.",
    "integrations.wizard_creds": "Wprowadź dane logowania z portalu dostawcy.",
    "integrations.ext_id": "Zewnętrzne ID restauracji",
    "integrations.api_key": "Klucz API / Secret",
    "integrations.continue": "Dalej",
    "integrations.back": "Wstecz",
    "integrations.complete": "Zakończ konfigurację",
    "integrations.connecting": "Łączenie…",
    "integrations.sandbox_mode": "Tryb piaskownicy (Sandbox)",
    "integrations.edit": "Edytuj",
    "integrations.delete": "Usuń",
    "integrations.delete_confirm": "Czy na pewno chcesz trwale usunąć tę integrację? Tej operacji nie można cofnąć.",
    "integrations.updated": "Integracja zaktualizowana",
    "integrations.deleted": "Integracja usunięta",
    "menu.title": "Menu",
    "menu.products": "Produkty",
    "menu.attributes": "Atrybuty",
    "menu.add_product": "+ Dodaj produkt",
    "menu.add_attribute": "+ Dodaj atrybut",
    "menu.name": "Nazwa",
    "menu.sku": "SKU",
    "menu.price": "Cena (grosze)",
    "menu.description": "Opis",
    "menu.active": "Aktywny",
    "menu.actions": "Akcje",
    "menu.save": "Zapisz",
    "menu.cancel": "Anuluj",
    "menu.delete": "Usuń",
    "menu.empty": "Brak produktów. Dodaj pierwszy produkt.",
    "menu.category": "Kategoria",
    "menu.add_category": "+ Dodaj kategorię",
    "menu.category_name": "Nazwa kategorii",
    "menu.attr_name": "Nazwa atrybutu",
    "settings.title": "Ustawienia",
    "settings.language": "Język",
    "settings.language_desc": "Wybierz preferowany język interfejsu.",
    "settings.printer": "Konfiguracja drukarki",
    "settings.printer_desc": "Skonfiguruj drukowanie paragonów.",
    "settings.printer_ip": "Adres IP drukarki",
    "settings.printer_name": "Nazwa drukarki",
    "settings.test_print": "🖨️ Wydruk testowy",
    "settings.auto_print": "Ustawienia auto-druku",
    "settings.auto_new": "Auto-druk przy nowym zamówieniu",
    "settings.auto_accept": "Auto-druk przy przyjęciu zamówienia",
    "settings.auto_reject": "Auto-druk przy odrzuceniu zamówienia",
    "settings.save": "Zapisz ustawienia",
    "settings.saved": "Ustawienia zapisane!",
    "settings.team": "Zespół",
    "settings.team_desc": "Kto widzi zamówienia i zarządza restauracją.",
    "settings.team_members": "Członkowie",
    "settings.team_invite": "Zaproś współpracownika",
    "settings.team_invite_email": "E-mail",
    "settings.team_invite_role": "Rola",
    "settings.team_invite_send": "Wyślij zaproszenie",
    "settings.team_inviting": "Tworzenie…",
    "settings.team_pending": "Oczekujące zaproszenia",
    "settings.team_no_pending": "Brak oczekujących zaproszeń.",
    "settings.team_link_ready": "Zaproszenie wysłane na adres {email}. Jeśli nie dotrze, skopiuj ten link.",
    "settings.team_copy": "Kopiuj link",
    "settings.team_revoke": "Anuluj",
    "settings.team_remove": "Usuń",
    "settings.team_remove_confirm": "Usunąć {email} z tej restauracji? Dostęp zostanie odebrany natychmiast.",
    "settings.team_removed": "Członek usunięty",
    "settings.team_invited": "Zaproszenie utworzone",
    "settings.team_revoked": "Zaproszenie anulowane",
    "settings.team_link_copied": "Link skopiowany",
    "settings.team_only_managers": "Tylko menedżerowie i właściciele mogą zapraszać i usuwać osoby.",
    "settings.team_you": "ty",
    "help.title": "Centrum pomocy",
    "help.faq": "Najczęściej zadawane pytania",
    "help.contact": "Kontakt",
    "help.faq1_q": "Jak podłączyć serwis dostawczy?",
    "help.faq1_a": "Przejdź do zakładki Integracje i kliknij '+ Dodaj integrację'. Wybierz platformę, wprowadź dane z portalu dostawcy i kliknij 'Zakończ konfigurację'.",
    "help.faq2_q": "Jak przyjąć lub odrzucić zamówienie?",
    "help.faq2_a": "Nowe zamówienia mają status 'received'. Kliknij '✅ Przyjmij' aby potwierdzić lub '❌ Odrzuć' aby odrzucić. Zmiana statusu jest przesyłana na platformę w czasie rzeczywistym.",
    "help.faq3_q": "Jak skonfigurować auto-druk?",
    "help.faq3_a": "Przejdź do Ustawienia → Konfiguracja drukarki. Wprowadź adres IP i nazwę drukarki, włącz przełączniki auto-druku. Kliknij 'Zapisz ustawienia'.",
    "help.faq4_q": "Jak zarządzać menu?",
    "help.faq4_a": "Przejdź do zakładki Menu. Dodaj produkty z nazwą, SKU i ceną. Przełącznik 'Aktywny' włącza/wyłącza produkty na wszystkich platformach jednocześnie.",
    "help.faq5_q": "Jak zmienić język interfejsu?",
    "help.faq5_a": "Przejdź do Ustawienia → Język i wybierz preferowany język. Zmiana jest natychmiastowa.",
    "help.faq6_q": "Jak działa synchronizacja multi-platformowa?",
    "help.faq6_a": "Zmiana dostępności produktu w Restify jest przesyłana na wszystkie podłączone platformy jednocześnie.",
    "toast.order_received": "Nowe zamówienie",
    "toast.order_received_body": "Zamówienie #{id} otrzymane",
    "toast.status_updated": "Status zaktualizowany",
    "toast.error": "Błąd",
    "toast.connected": "Połączono",
    "toast.settings_saved": "Ustawienia zapisane",
    "generic.loading": "Ładowanie…",
    "generic.orders": "zamówień",
    "generic.no_membership": "Twoje konto nie ma przypisanej restauracji — poproś właściciela o zaproszenie.",
    // Onboarding Wizard
    "onboarding.step_welcome": "Witaj",
    "onboarding.step_connect": "Połącz",
    "onboarding.step_test": "Test",
    "onboarding.step_live": "Uruchom",
    "onboarding.step_complete": "Gotowe",
    "onboarding.welcome_title": "Skonfigurujmy Twoją restaurację!",
    "onboarding.welcome_desc": "Poprowadzimy Cię przez podłączenie pierwszej platformy dostawczej i weryfikację przepływu zamówień.",
    "onboarding.checklist_connect": "Podłącz platformę dostawczą",
    "onboarding.checklist_connect_desc": "Połącz konto Wolt, Uber Eats, Glovo, Pyszne.pl lub Bolt Food.",
    "onboarding.checklist_test": "Wyślij zamówienie testowe",
    "onboarding.checklist_test_desc": "Sprawdź integrację za pomocą symulacji sandbox.",
    "onboarding.checklist_live": "Uruchom na żywo",
    "onboarding.checklist_live_desc": "Wyłącz sandbox i zacznij odbierać prawdziwe zamówienia.",
    "onboarding.get_started": "Rozpocznij",
    "onboarding.connect_title": "Podłącz platformę dostawczą",
    "onboarding.connect_desc": "Wybierz platformę, którą chcesz zintegrować najpierw. Kolejne dodasz później.",
    "onboarding.change_platform": "Zmień",
    "onboarding.ext_id_placeholder": "np. 12345",
    "onboarding.chain_id_label": "ID łańcucha Delivery Hero (opcjonalnie)",
    "onboarding.chain_id_placeholder": "np. chain-12345",
    "onboarding.chain_id_hint": "Tylko Glovo. Pozostaw puste dla starszego API Glovo.",
    "onboarding.secret_placeholder": "Wklej tutaj swój klucz",
    "onboarding.optional_for_sandbox": "opcjonalne dla sandbox",
    "onboarding.webhook_url_label": "URL webhooka (zarejestruj w portalu dostawcy)",
    "onboarding.webhook_url_hint": "Skopiuj ten URL i zarejestruj go w ustawieniach webhooka platformy.",
    "onboarding.copy": "Kopiuj",
    "onboarding.connect_and_test": "Połącz i kontynuuj",
    "onboarding.test_title": "Przetestuj połączenie",
    "onboarding.test_desc": "Twoja integracja jest w trybie sandbox. Wyślijmy symulowane zamówienie, aby sprawdzić, czy wszystko działa.",
    "onboarding.sandbox_active": "SANDBOX",
    "onboarding.send_test_order": "Wyślij zamówienie testowe",
    "onboarding.sending_test": "Wysyłanie…",
    "onboarding.test_success": "Zamówienie testowe wysłane!",
    "onboarding.test_success_desc": "Symulowane zamówienie zostało przetworzone pomyślnie. Sprawdź zakładkę Zamówienia.",
    "onboarding.retry": "Spróbuj ponownie",
    "onboarding.go_live_title": "Włącz prawdziwe zamówienia",
    "onboarding.go_live_desc": "Test sandbox zaliczony. Wpisz prawdziwe dane z portalu dostawcy i włącz zamówienia na żywo. Tryb live jest zablokowany, dopóki wszystkie kontrole nie przejdą.",
    "onboarding.live_secret_label": "Prawdziwy sekret webhooka",
    "onboarding.live_secret_placeholder": "Prawdziwy sekret z portalu dostawcy",
    "onboarding.live_secret_hint": "Pozostaw puste, jeśli prawdziwy sekret wpisano w poprzednim kroku.",
    "onboarding.live_readiness": "Kontrole połączenia",
    "onboarding.live_not_ready": "Połączenie nie jest jeszcze gotowe — popraw niezaliczone kontrole i sprawdź ponownie.",
    "onboarding.live_oauth_pending": "OAuth Wolt nie jest ukończone. Dokończ autoryzację po stronie Wolt i naciśnij „Sprawdź ponownie”.",
    "onboarding.check_external_restaurant_id": "ID obiektu/sklepu dostawcy",
    "onboarding.check_webhook_secret": "Prawdziwy sekret webhooka",
    "onboarding.check_provider_config": "Konfiguracja dostawcy",
    "onboarding.check_oauth": "Tokeny OAuth",
    "onboarding.refresh_readiness": "Sprawdź ponownie",
    "onboarding.go_live": "Włącz zamówienia na żywo",
    "onboarding.going_live": "Włączanie…",
    "onboarding.complete_title": "Wszystko gotowe!",
    "onboarding.complete_desc": "Twoja restauracja jest podłączona i gotowa do odbierania zamówień.",
    "onboarding.summary_restaurant": "Restauracja utworzona",
    "onboarding.summary_integration": "{platform} podłączona",
    "onboarding.summary_test": "Zamówienie testowe zweryfikowane",
    "onboarding.next_steps": "Zalecane następne kroki",
    "onboarding.next_webhook": "Zarejestruj URL webhooka w portalu dostawcy, aby odbierać prawdziwe zamówienia.",
    "onboarding.next_more_platforms": "Podłącz dodatkowe platformy dostawcze w zakładce Integracje.",
    "onboarding.next_printer": "Skonfiguruj drukarkę paragonów w Ustawieniach.",
    "onboarding.next_menu": "Zaimportuj menu w zakładce Menu, aby synchronizować dostępność.",
    "onboarding.go_to_dashboard": "Przejdź do panelu",
    "onboarding.skip": "Pomiń konfigurację — zrobię to później",
  },
  ru: {
    "header.title": "RESTIFY · Панель",
    "header.signout": "Выйти",
    // Billing
    "billing.trial_days": "Пробный период: осталось {days} дн.",
    "billing.trial_expired": "Пробный период закончился.",
    "billing.trial_hint": "Выберите тариф, чтобы сохранить все функции.",
    "billing.upgrade": "Выбрать тариф",
    "billing.unavailable": "Оплата пока недоступна — обратитесь в поддержку.",
    "billing.error": "Не удалось начать оплату",
    "login.title": "RESTIFY",
    "login.subtitle": "Войдите в панель управления",
    "login.subtitle_register": "Создайте аккаунт",
    "login.email": "E-mail",
    "login.password": "Пароль",
    "login.fullname": "Полное имя",
    "login.signin": "Войти",
    "login.signing_in": "Вход…",
    "login.create": "Создать аккаунт",
    "login.creating": "Создание…",
    "login.toggle_register": "Нет аккаунта? Создать",
    "login.toggle_login": "Уже есть аккаунт? Войти",
    "tab.orders": "Заказы",
    "tab.analytics": "Аналитика",
    "tab.integrations": "Интеграции",
    "tab.menu": "Меню",
    "tab.settings": "Настройки",
    "tab.help": "Помощь",
    "orders.title": "Заказы в реальном времени",
    "orders.search": "Поиск заказов, товаров, клиентов…",
    "orders.empty": "Пока нет заказов — ожидание вебхуков…",
    "orders.empty_filter": "Нет заказов по вашим фильтрам",
    "orders.connect": "🔄 Обновить экран",
    "orders.all_platforms": "Все платформы",
    "orders.all_statuses": "Все статусы",
    "orders.accept": "✅ Принять",
    "orders.reject": "❌ Отклонить",
    "orders.hide": "✕ Скрыть",
    "orders.clear_completed": "🧹 Очистить",
    "orders.print": "🖨️ Печать",
    "analytics.title": "Аналитика · Последние 14 дней",
    "analytics.total": "Всего заказов",
    "analytics.revenue": "Выручка",
    "analytics.avg": "Ср. заказ",
    "analytics.cancelled": "Отменённые",
    "analytics.active": "Активные сейчас",
    "adv_analytics.days": "дней",
    "adv_analytics.export_csv": "Экспорт CSV",
    "adv_analytics.export_pdf": "Печать / PDF",
    "adv_analytics.export_failed": "Экспорт не выполнен",
    "adv_analytics.hourly_title": "Заказы по часам",
    "adv_analytics.platform_revenue": "Выручка по платформам",
    "adv_analytics.avg_check_title": "Средний чек по платформам",
    "adv_analytics.avg_check": "Ср. чек",
    "adv_analytics.top_dishes_title": "Топ блюд",
    "adv_analytics.dish_name": "Блюдо",
    "adv_analytics.quantity": "Продано",
    "adv_analytics.platform": "Платформа",
    "adv_analytics.reconciliation_title": "Сверка выручки",
    "adv_analytics.fees": "Комиссии",
    "adv_analytics.net": "Нетто",
    "adv_analytics.no_data": "Нет данных за выбранный период.",
    "integrations.title": "Интеграции",
    "integrations.add": "+ Добавить интеграцию",
    "integrations.desc": "Управляйте подключениями к платформам доставки.",
    "integrations.empty": "Нет подключённых интеграций.",
    "integrations.wizard_title": "Подключить сервис",
    "integrations.wizard_desc": "Выберите платформу доставки для интеграции.",
    "integrations.wizard_creds": "Введите учётные данные из портала поставщика.",
    "integrations.ext_id": "Внешний ID ресторана",
    "integrations.api_key": "API-ключ / Секрет",
    "integrations.continue": "Далее",
    "integrations.back": "Назад",
    "integrations.complete": "Завершить настройку",
    "integrations.connecting": "Подключение…",
    "integrations.sandbox_mode": "Режим песочницы (Sandbox)",
    "integrations.edit": "Изменить",
    "integrations.delete": "Удалить",
    "integrations.delete_confirm": "Вы уверены, что хотите полностью удалить эту интеграцию? Это действие нельзя отменить.",
    "integrations.updated": "Интеграция обновлена",
    "integrations.deleted": "Интеграция удалена",
    "menu.title": "Меню",
    "menu.products": "Продукты",
    "menu.attributes": "Атрибуты",
    "menu.add_product": "+ Добавить продукт",
    "menu.add_attribute": "+ Добавить атрибут",
    "menu.name": "Название",
    "menu.sku": "Артикул",
    "menu.price": "Цена (копейки)",
    "menu.description": "Описание",
    "menu.active": "Активен",
    "menu.actions": "Действия",
    "menu.save": "Сохранить",
    "menu.cancel": "Отмена",
    "menu.delete": "Удалить",
    "menu.empty": "Нет продуктов. Добавьте первый продукт.",
    "menu.category": "Категория",
    "menu.add_category": "+ Добавить категорию",
    "menu.category_name": "Название категории",
    "menu.attr_name": "Название атрибута",
    "settings.title": "Настройки",
    "settings.language": "Язык",
    "settings.language_desc": "Выберите предпочтительный язык интерфейса.",
    "settings.printer": "Настройка принтера",
    "settings.printer_desc": "Настройте печать чеков для заказов.",
    "settings.printer_ip": "IP-адрес принтера",
    "settings.printer_name": "Имя принтера",
    "settings.test_print": "🖨️ Тестовая печать",
    "settings.auto_print": "Настройки авто-печати",
    "settings.auto_new": "Авто-печать при новом заказе",
    "settings.auto_accept": "Авто-печать при принятии заказа",
    "settings.auto_reject": "Авто-печать при отклонении заказа",
    "settings.save": "Сохранить настройки",
    "settings.saved": "Настройки сохранены!",
    "settings.team": "Команда",
    "settings.team_desc": "Кто видит заказы и управляет рестораном.",
    "settings.team_members": "Участники",
    "settings.team_invite": "Пригласить сотрудника",
    "settings.team_invite_email": "Email",
    "settings.team_invite_role": "Роль",
    "settings.team_invite_send": "Отправить приглашение",
    "settings.team_inviting": "Создание…",
    "settings.team_pending": "Ожидающие приглашения",
    "settings.team_no_pending": "Нет ожидающих приглашений.",
    "settings.team_link_ready": "Приглашение отправлено на {email}. Если письмо не придёт, скопируйте ссылку.",
    "settings.team_copy": "Копировать ссылку",
    "settings.team_revoke": "Отозвать",
    "settings.team_remove": "Удалить",
    "settings.team_remove_confirm": "Удалить {email} из ресторана? Доступ будет отозван сразу.",
    "settings.team_removed": "Участник удалён",
    "settings.team_invited": "Приглашение создано",
    "settings.team_revoked": "Приглашение отозвано",
    "settings.team_link_copied": "Ссылка скопирована",
    "settings.team_only_managers": "Приглашать и удалять участников могут только менеджеры и владельцы.",
    "settings.team_you": "вы",
    "help.title": "Центр помощи",
    "help.faq": "Часто задаваемые вопросы",
    "help.contact": "Связаться с нами",
    "help.faq1_q": "Как подключить сервис доставки?",
    "help.faq1_a": "Перейдите на вкладку Интеграции и нажмите '+ Добавить интеграцию'. Выберите платформу, введите данные из портала поставщика и нажмите 'Завершить настройку'.",
    "help.faq2_q": "Как принять или отклонить заказ?",
    "help.faq2_a": "Новые заказы имеют статус 'received'. Нажмите '✅ Принять' для подтверждения или '❌ Отклонить' для отказа. Изменение статуса отправляется на платформу в реальном времени.",
    "help.faq3_q": "Как настроить авто-печать?",
    "help.faq3_a": "Перейдите в Настройки → Настройка принтера. Введите IP-адрес и имя принтера, включите переключатели авто-печати. Нажмите 'Сохранить настройки'.",
    "help.faq4_q": "Как управлять меню?",
    "help.faq4_a": "Перейдите на вкладку Меню. Добавьте продукты с названием, артикулом и ценой. Переключатель 'Активен' включает/выключает продукты на всех платформах одновременно.",
    "help.faq5_q": "Как изменить язык интерфейса?",
    "help.faq5_a": "Перейдите в Настройки → Язык и выберите предпочтительный язык. Изменение применяется немедленно.",
    "help.faq6_q": "Как работает мультиплатформенная синхронизация?",
    "help.faq6_a": "Изменение доступности продукта в Restify автоматически отправляется на все подключённые платформы одновременно.",
    "toast.order_received": "Новый заказ",
    "toast.order_received_body": "Заказ #{id} получен",
    "toast.status_updated": "Статус обновлён",
    "toast.error": "Ошибка",
    "toast.connected": "Подключено",
    "toast.settings_saved": "Настройки сохранены",
    "generic.loading": "Загрузка…",
    "generic.orders": "заказов",
    "generic.no_membership": "У вашего аккаунта нет привязки к ресторану — попросите владельца пригласить вас.",
    // Onboarding Wizard
    "onboarding.step_welcome": "Добро пожаловать",
    "onboarding.step_connect": "Подключение",
    "onboarding.step_test": "Тест",
    "onboarding.step_live": "Запуск",
    "onboarding.step_complete": "Готово",
    "onboarding.welcome_title": "Настроим ваш ресторан!",
    "onboarding.welcome_desc": "Мы проведём вас через подключение первой платформы доставки и проверку поступления заказов.",
    "onboarding.checklist_connect": "Подключите платформу доставки",
    "onboarding.checklist_connect_desc": "Свяжите аккаунт Wolt, Uber Eats, Glovo, Pyszne.pl или Bolt Food.",
    "onboarding.checklist_test": "Отправьте тестовый заказ",
    "onboarding.checklist_test_desc": "Проверьте интеграцию с помощью симуляции в песочнице.",
    "onboarding.checklist_live": "Запуск в работу",
    "onboarding.checklist_live_desc": "Отключите песочницу и начните принимать реальные заказы.",
    "onboarding.get_started": "Начать",
    "onboarding.connect_title": "Подключите платформу доставки",
    "onboarding.connect_desc": "Выберите платформу для первой интеграции. Остальные можно добавить позже.",
    "onboarding.change_platform": "Изменить",
    "onboarding.ext_id_placeholder": "напр. 12345",
    "onboarding.chain_id_label": "ID цепочки Delivery Hero (необязательно)",
    "onboarding.chain_id_placeholder": "напр. chain-12345",
    "onboarding.chain_id_hint": "Только для Glovo. Оставьте пустым для старого API Glovo.",
    "onboarding.secret_placeholder": "Вставьте ваш секретный ключ",
    "onboarding.optional_for_sandbox": "необязательно для песочницы",
    "onboarding.webhook_url_label": "URL вебхука (зарегистрируйте в портале провайдера)",
    "onboarding.webhook_url_hint": "Скопируйте этот URL и зарегистрируйте его в настройках вебхуков вашей платформы.",
    "onboarding.copy": "Копировать",
    "onboarding.connect_and_test": "Подключить и продолжить",
    "onboarding.test_title": "Проверьте подключение",
    "onboarding.test_desc": "Ваша интеграция в режиме песочницы. Отправим тестовый заказ, чтобы убедиться, что всё работает.",
    "onboarding.sandbox_active": "ПЕСОЧНИЦА",
    "onboarding.send_test_order": "Отправить тестовый заказ",
    "onboarding.sending_test": "Отправка…",
    "onboarding.test_success": "Тестовый заказ отправлен!",
    "onboarding.test_success_desc": "Симулированный заказ обработан успешно. Проверьте вкладку Заказы.",
    "onboarding.retry": "Попробовать снова",
    "onboarding.go_live_title": "Включите реальные заказы",
    "onboarding.go_live_desc": "Тест в песочнице пройден. Введите реальные данные из портала провайдера и включите реальные заказы. Live-режим заблокирован, пока не пройдут все проверки.",
    "onboarding.live_secret_label": "Реальный секрет вебхука",
    "onboarding.live_secret_placeholder": "Реальный секрет из портала провайдера",
    "onboarding.live_secret_hint": "Оставьте пустым, если реальный секрет уже введён на предыдущем шаге.",
    "onboarding.live_readiness": "Проверки подключения",
    "onboarding.live_not_ready": "Подключение ещё не готово — исправьте непройденные проверки и проверьте снова.",
    "onboarding.live_oauth_pending": "OAuth Wolt не завершён. Завершите авторизацию на стороне Wolt и нажмите «Проверить снова».",
    "onboarding.check_external_restaurant_id": "ID заведения/магазина провайдера",
    "onboarding.check_webhook_secret": "Реальный секрет вебхука",
    "onboarding.check_provider_config": "Конфигурация провайдера",
    "onboarding.check_oauth": "OAuth-токены",
    "onboarding.refresh_readiness": "Проверить снова",
    "onboarding.go_live": "Включить реальные заказы",
    "onboarding.going_live": "Включение…",
    "onboarding.complete_title": "Всё готово!",
    "onboarding.complete_desc": "Ваш ресторан подключён и готов принимать заказы.",
    "onboarding.summary_restaurant": "Ресторан создан",
    "onboarding.summary_integration": "{platform} подключена",
    "onboarding.summary_test": "Тестовый заказ проверен",
    "onboarding.next_steps": "Рекомендуемые следующие шаги",
    "onboarding.next_webhook": "Зарегистрируйте URL вебхука в портале провайдера для получения реальных заказов.",
    "onboarding.next_more_platforms": "Подключите дополнительные платформы во вкладке Интеграции.",
    "onboarding.next_printer": "Настройте принтер чеков в Настройках для автоматической печати.",
    "onboarding.next_menu": "Импортируйте меню во вкладке Меню для синхронизации доступности.",
    "onboarding.go_to_dashboard": "Перейти к панели",
    "onboarding.skip": "Пропустить настройку — сделаю позже",
  },
};

type I18nContextValue = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string>) => string;
};

const I18nContext = createContext<I18nContextValue>({
  locale: "en",
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem("restify.locale") as Locale | null;
    return stored && dict[stored] ? stored : "en";
  });

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem("restify.locale", l);
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      let val = dict[locale]?.[key] ?? dict.en[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          val = val.replace(`{${k}}`, v);
        }
      }
      return val;
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  return useContext(I18nContext);
}
